import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { ErrorDetail } from '@core/models/api-response.model';
import { AppLoggerService, LOG_CATEGORY, newLogId } from '../services/app-logger.service';

const CORRELATION_HEADER = 'X-Correlation-ID';
// `/auth/me` answers 401 for "not signed in" — the question being asked, not a failure.
const SESSION_PROBE = '/auth/me';

function isExpectedFailure(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
  return error.status === 401 && req.url.includes(SESSION_PROBE);
}

function apiPath(url: string): string {
  return url.slice(environment.apiBaseUrl.length).split('?')[0];
}

/**
 * Tags every API call with an `X-Correlation-ID` (the API adopts it for all of that request's server-side
 * log lines) and reports each failed call to {@link AppLoggerService} with the same id — so a UI entry in
 * Seq leads straight to the server's account of the same request. Network failures (status 0) never reach
 * the server's logs at all; this is the only record of them.
 */
export const httpLoggingInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl)) return next(req);

  const logger = inject(AppLoggerService);
  const correlationId = newLogId();

  return next(req.clone({ setHeaders: { [CORRELATION_HEADER]: correlationId } })).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && !isExpectedFailure(req, error)) {
        const body = error.error as { message?: string; error?: ErrorDetail | null } | null;
        const path = apiPath(req.url);
        logger.log({
          level: error.status === 0 || error.status >= 500 ? 'error' : 'warning',
          category: LOG_CATEGORY.HTTP,
          message:
            error.status === 0
              ? `Network error calling ${req.method} ${path}`
              : `HTTP ${error.status} on ${req.method} ${path}`,
          httpStatus: error.status,
          httpMethod: req.method,
          requestUrl: path,
          correlationId: correlationId,
          properties: {
            ApiMessage: body?.error?.message ?? body?.message,
            ApiErrorCode: body?.error?.code,
          },
        });
      }
      return throwError(() => error);
    })
  );
};
